import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Spinner, fmtKg } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { ArrowLeft, Save, Tractor, MessageSquare, Plus, Truck } from "lucide-react";

export default function InvoiceNew() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [suppliers, setSuppliers] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    nota_numero: "",
    fornecedor: "",
    tipo_cebola: "amarela",
    unidade: "Matriz VGS",
    peso_bruto: "",
    peso_tara: "",
    valor_nf: "",
    data_entrada: new Date().toISOString().slice(0, 10),
    motorista: "",
    placa: "",
    origem: "",
    observacoes: "",
    ja_beneficiada: false,
    // Frete
    frete_modo: "por_ton",       // "por_ton" | "total"
    frete_por_ton: "",
    frete_total_manual: "",
  });

  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/motoristas").then((r) => setMotoristas((r.data || []).filter(m => m.ativo !== false))).catch(() => {}); }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/invoices/${id}`)
      .then((r) => {
        const inv = r.data || {};
        setForm({
          nota_numero: inv.nota_numero || "",
          fornecedor: inv.fornecedor || "",
          tipo_cebola: inv.tipo_cebola || "amarela",
          unidade: inv.unidade || "Matriz VGS",
          peso_bruto: inv.peso_bruto ?? "",
          peso_tara: inv.peso_tara ?? "",
          valor_nf: inv.valor_nf ?? "",
          data_entrada: (inv.data_entrada || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
          motorista: inv.motorista || "",
          placa: inv.placa || "",
          origem: inv.origem || "",
          observacoes: inv.observacoes || "",
          ja_beneficiada: !!inv.ja_beneficiada,
          // Frete — se já tem custos.frete salvo, reidrata para o modo "por_ton"
          frete_modo: "por_ton",
          frete_por_ton: (inv.beneficiamento?.custos?.frete ?? "") || "",
          frete_total_manual: "",
          // Terceiros — preserva para não perder no PUT
          tipo_operacao: inv.tipo_operacao || "propria",
          cliente_dono: inv.cliente_dono || "",
          cliente_dono_cnpj: inv.cliente_dono_cnpj || "",
          preco_servico_sc: inv.preco_servico_sc ?? 3,
          preco_sacaria_sc: inv.preco_sacaria_sc ?? 2,
          usa_sacaria_propria: !!inv.usa_sacaria_propria,
        });
      })
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pesoLiquido = (Number(form.peso_bruto) || 0) - (Number(form.peso_tara) || 0);

  const submit = async (goToBenef) => {
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        peso_bruto: Number(form.peso_bruto) || 0,
        peso_tara: Number(form.peso_tara) || 0,
        peso_liquido: pesoLiquido,
        valor_nf: Number(form.valor_nf) || 0,
        frete_por_ton: form.frete_modo === "por_ton" ? (Number(form.frete_por_ton) || 0) : 0,
        frete_total_manual: form.frete_modo === "total" ? (Number(form.frete_total_manual) || 0) : 0,
      };
      // Remove chave só-UI (backend não conhece)
      delete payload.frete_modo;
      if (isEdit) {
        await api.put(`/invoices/${id}`, payload);
        nav(`/invoices/${id}`);
        return;
      }
      const { data } = await api.post("/invoices", payload);
      if (goToBenef) {
        await api.post(`/invoices/${data.id}/iniciar-beneficiamento`);
        nav(`/invoices/${data.id}/beneficiamento`);
      } else {
        nav(`/invoices`);
      }
    } catch (e2) { setErr(formatErr(e2)); setBusy(false); }
  };

  if (loading) {
    return (
      <Layout title="Editar Nota Fiscal" subtitle="Carregando dados...">
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      </Layout>
    );
  }

  return (
    <Layout title={isEdit ? `Editar NF #${form.nota_numero || ""}` : "Nova Nota Fiscal"} subtitle={isEdit ? "Atualização de dados da entrada" : "Registro de entrada de matéria-prima"}
      actions={<Button variant="secondary" onClick={() => nav(isEdit ? `/invoices/${id}` : "/invoices")}><ArrowLeft size={16} /> Voltar</Button>}>
      <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Identificação</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Número da NF" required value={form.nota_numero} onChange={set("nota_numero")} data-testid="nf-numero" />
              <Input label="Data de entrada" type="date" required value={form.data_entrada} onChange={set("data_entrada")} data-testid="nf-data" />
              <Select label="Fornecedor" required value={form.fornecedor} onChange={set("fornecedor")} data-testid="nf-fornecedor">
                <option value="">Selecione...</option>
                {suppliers.map((s) => <option key={s.id} value={s.nome}>{s.nome}</option>)}
              </Select>
              <Select label="Tipo de cebola" value={form.tipo_cebola} onChange={set("tipo_cebola")} data-testid="nf-tipo">
                <option value="amarela">Amarela</option>
                <option value="roxa">Roxa</option>
                <option value="branca">Branca</option>
                <option value="pelada">Pelada</option>
                <option value="bruta">🌾 Bruta (a granel, por kg)</option>
              </Select>
              {form.tipo_cebola === "bruta" && (
                <div className="md:col-span-3 -mt-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="nf-bruta-hint">
                  <strong>Cebola Bruta:</strong> essa NF <u>não passará por beneficiamento</u>. Ao salvar, o sistema cria automaticamente <strong>1 etiqueta única</strong> com o peso líquido total da carga (em kg) e marca a NF como já beneficiada. A saída será registrada por <strong>peso (kg)</strong> na Expedição.
                </div>
              )}
              <Select label="Unidade de beneficiamento" required value={form.unidade} onChange={set("unidade")} data-testid="nf-unidade">
                <option value="Matriz VGS">Matriz VGS</option>
                <option value="Araxá">Araxá</option>
                <option value="Ituporanga">Ituporanga</option>
              </Select>
              <Input label="Origem (cidade/UF)" value={form.origem} onChange={set("origem")} data-testid="nf-origem" />
              <Input label="Valor da NF (R$)" type="number" step="0.01" value={form.valor_nf} onChange={set("valor_nf")} data-testid="nf-valor" />
            </div>
            <label className="mt-4 flex items-start gap-3 p-3 rounded-md border border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors">
              <input
                type="checkbox"
                checked={!!form.ja_beneficiada}
                onChange={(e) => setForm({ ...form, ja_beneficiada: e.target.checked })}
                className="mt-0.5 cursor-pointer"
                data-testid="nf-ja-beneficiada"
              />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">Já beneficiada (comprada pronta)</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Marque quando a mercadoria já veio maquinada de fora. Assim ela não entra no
                  cálculo de <b>custo por saco</b> (afinal, não usou máquina/mão de obra daqui).
                </div>
              </div>
            </label>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Pesagem</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Peso bruto (kg)" type="number" step="0.01" required value={form.peso_bruto} onChange={set("peso_bruto")} data-testid="nf-peso-bruto" />
              <Input label="Tara (kg)" type="number" step="0.01" value={form.peso_tara} onChange={set("peso_tara")} data-testid="nf-peso-tara" />
              <div>
                <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Peso líquido</div>
                <div className="font-mono text-2xl font-semibold text-brand-600 px-3 py-2 bg-emerald-50 rounded-md border border-emerald-100" data-testid="nf-peso-liquido">
                  {fmtKg(pesoLiquido)}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4 flex items-center gap-2"><Truck size={18} /> Transporte</h3>
            {motoristas.length === 0 ? (
              <div className="p-4 rounded-md bg-amber-50 border border-amber-300 text-sm text-amber-900">
                Nenhum motorista cadastrado ainda.{" "}
                <a href="/motoristas" target="_blank" rel="noreferrer" className="font-bold underline hover:text-amber-700">
                  Cadastrar motoristas agora <Plus size={12} className="inline" />
                </a>
                {" "}(abre em nova aba — recarregue esta página depois).
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Motorista"
                  value={form.motorista}
                  onChange={(e) => {
                    const nome = e.target.value;
                    const m = motoristas.find((mm) => mm.nome === nome);
                    // Ao trocar motorista, se placa atual não pertence a ele, seleciona a 1ª placa dele
                    const placasDele = (m?.placas || []);
                    const novaPlaca = placasDele.includes(form.placa) ? form.placa : (placasDele[0] || "");
                    setForm({ ...form, motorista: nome, placa: novaPlaca });
                  }}
                  data-testid="nf-motorista"
                >
                  <option value="">Selecione um motorista...</option>
                  {motoristas.map((m) => (
                    <option key={m.id} value={m.nome}>{m.nome}{m.cpf ? ` — ${m.cpf}` : ""}</option>
                  ))}
                </Select>
                <Select
                  label="Placa"
                  value={form.placa}
                  onChange={set("placa")}
                  disabled={!form.motorista}
                  data-testid="nf-placa"
                >
                  <option value="">{form.motorista ? "Selecione a placa..." : "Escolha o motorista primeiro"}</option>
                  {(motoristas.find((m) => m.nome === form.motorista)?.placas || []).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Select>
              </div>
            )}
            <div className="mt-2 text-xs text-ink-500">
              <a href="/motoristas" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
                + Cadastrar novo motorista ou placa
              </a>
            </div>

            {/* Frete do motorista */}
            <div className="mt-5 pt-5 border-t border-ink-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-heading font-bold text-ink-900">Frete do motorista</h4>
                <div className="inline-flex rounded-md border border-ink-300 overflow-hidden text-xs" data-testid="frete-modo-toggle">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, frete_modo: "por_ton" })}
                    className={`px-3 py-1.5 font-semibold ${form.frete_modo === "por_ton" ? "bg-brand-500 text-white" : "bg-white text-ink-700 hover:bg-ink-50"}`}
                    data-testid="frete-modo-por-ton"
                  >R$ / tonelada</button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, frete_modo: "total" })}
                    className={`px-3 py-1.5 font-semibold ${form.frete_modo === "total" ? "bg-brand-500 text-white" : "bg-white text-ink-700 hover:bg-ink-50"}`}
                    data-testid="frete-modo-total"
                  >R$ total</button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {form.frete_modo === "por_ton" ? (
                  <Input
                    label="Frete por tonelada líquida (R$/t)"
                    type="number"
                    step="0.01"
                    value={form.frete_por_ton}
                    onChange={set("frete_por_ton")}
                    placeholder="Ex.: 120.00"
                    data-testid="frete-por-ton"
                  />
                ) : (
                  <Input
                    label="Valor total do frete (R$)"
                    type="number"
                    step="0.01"
                    value={form.frete_total_manual}
                    onChange={set("frete_total_manual")}
                    placeholder="Ex.: 1500.00"
                    data-testid="frete-total-manual"
                  />
                )}
                <div className="p-3 rounded-md bg-emerald-50 border border-emerald-100">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Preview</div>
                  {(() => {
                    const tonLiq = pesoLiquido / 1000;
                    let porTon = 0, total = 0;
                    if (form.frete_modo === "por_ton") {
                      porTon = Number(form.frete_por_ton) || 0;
                      total = porTon * tonLiq;
                    } else {
                      total = Number(form.frete_total_manual) || 0;
                      porTon = tonLiq > 0 ? total / tonLiq : 0;
                    }
                    return (
                      <div className="mt-1 space-y-0.5" data-testid="frete-preview">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs text-ink-500">Total:</span>
                          <span className="font-mono font-bold text-lg text-emerald-700">R$ {total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="text-[11px] text-ink-500">
                          {porTon > 0 && tonLiq > 0 ? (
                            <>R$ {porTon.toFixed(2)}/t × {tonLiq.toFixed(3)} t líquidas</>
                          ) : (
                            <>Preencha peso líquido acima para calcular</>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {form.motorista && (Number(form.frete_por_ton) > 0 || Number(form.frete_total_manual) > 0) && (
                <div className="mt-3 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2" data-testid="frete-auto-ap-hint">
                  <span className="mt-0.5">💡</span>
                  <div>
                    <strong>Automático:</strong> ao salvar, o valor irá para <b>Preços &amp; Custos</b> e um lançamento em <b>Fretes a Pagar</b> será criado para <b>{form.motorista}</b>.
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4 flex items-center gap-2"><MessageSquare size={18} /> Observações</h3>
            <textarea
              value={form.observacoes} onChange={set("observacoes")} rows={3}
              placeholder="Anotações livres sobre a entrega..."
              className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
              data-testid="nf-observacoes"
            />
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-6 sticky top-20">
            <h3 className="font-heading font-bold text-ink-900 mb-2">{isEdit ? "Editar NF" : "Resumo"}</h3>
            <p className="text-sm text-ink-500 mb-4">{isEdit ? "Ajuste os campos e salve as alterações." : "Escolha se quer apenas salvar ou já iniciar o beneficiamento."}</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-500">NF</span><span className="font-mono">{form.nota_numero || "—"}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Fornecedor</span><span className="truncate">{form.fornecedor || "—"}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Líquido</span><span className="font-mono">{fmtKg(pesoLiquido)}</span></div>
            </div>
            {err && <div className="text-sm text-danger mt-4 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
            <div className="space-y-2 mt-5">
              <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="nf-submit">
                {busy ? <Spinner size={16} /> : <><Save size={16} /> {isEdit ? "Salvar Alterações" : "Apenas Salvar"}</>}
              </Button>
              {!isEdit && (
                <Button type="button" variant="secondary" size="lg" className="w-full" disabled={busy} onClick={() => submit(true)} data-testid="nf-submit-benef">
                  {busy ? <Spinner size={16} /> : <><Tractor size={16} /> Salvar + Iniciar Beneficiamento</>}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </form>
    </Layout>
  );
}
